import { AlertTriangle, CheckCircle2, ShieldX, Skull, X } from "lucide-react";

import { formatQuantity } from "../lib/format";
import { useModalFocus } from "../lib/useModalFocus";
import type { BattleReport, GameState } from "../types";
import { EventArtwork, type EventArtworkVariant } from "./EventArtwork";
import { SurvivorPortrait } from "./SurvivorPortrait";

export function BattleReportDialog({ report, state, onClose }: { report: BattleReport; state: GameState; onClose: () => void }) {
  const outcome = { repelled: { title: "The line held.", icon: CheckCircle2 }, costly_victory: { title: "The bunker survived. Barely.", icon: AlertTriangle }, overrun: { title: "The bunker has fallen.", icon: ShieldX } }[report.outcome];
  const artwork: Record<BattleReport["outcome"], { variant: EventArtworkVariant; caption: string }> = {
    repelled: { variant: "contact", caption: "After-action reconstruction · entrance held" },
    costly_victory: { variant: "breach", caption: "After-action reconstruction · fallback line" },
    overrun: { variant: "overrun", caption: "After-action reconstruction · final withdrawal" },
  };
  const OutcomeIcon = outcome.icon;
  const modalRef = useModalFocus<HTMLDivElement>({
    focusKey: report.outcome,
    onEscape: onClose,
  });

  return (
    <div
      ref={modalRef}
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-title"
      aria-describedby="report-description"
      tabIndex={-1}
    >
      <section className={`battle-report report-${report.outcome}`}>
        <button className="modal-close" onClick={onClose} aria-label="Close battle report">
          <X aria-hidden="true" />
        </button>
        <OutcomeIcon className="report-icon" aria-hidden="true" />
        <span className="eyebrow">After-action report</span>
        <h2 id="report-title">{outcome.title}</h2>
        <p id="report-description">{report.events.at(-1)?.message}</p>
        <EventArtwork
          variant={artwork[report.outcome].variant}
          caption={artwork[report.outcome].caption}
        />
        {(report.fallback_engaged ||
          report.ordered_evacuation ||
          Boolean(report.ammunition_reserved)) && (
          <div className="crisis-intel report-orders" aria-label="Standing order results">
            {report.fallback_engaged && (
              <span>
                <i>
                  <small>Fallback order</small>
                  <strong>Executed</strong>
                </i>
              </span>
            )}
            {report.ordered_evacuation && (
              <span>
                <i>
                  <small>Evacuation threshold</small>
                  <strong>Triggered</strong>
                </i>
              </span>
            )}
            {Boolean(report.ammunition_reserved) && (
              <span>
                <i>
                  <small>Ammunition held back</small>
                  <strong>{formatQuantity(report.ammunition_reserved ?? 0)}</strong>
                </i>
              </span>
            )}
          </div>
        )}
        <div className="report-numbers">
          <span><strong>{formatQuantity(report.ammo_used)}</strong><small>ammunition used</small></span>
          <span><strong>{report.injuries.length}</strong><small>wounded</small></span>
          <span><strong>{report.damaged_spaces.length}</strong><small>rooms damaged</small></span>
          <span className={report.deaths.length ? "losses" : ""}><strong>{report.deaths.length}</strong><small>people lost</small></span>
        </div>
        {report.deaths.length > 0 && (
          <div className="report-loss-list">
            <h3><Skull size={15} aria-hidden="true" />We remember</h3>
            {report.deaths.map((id) => {
              const survivor = state.survivors.find((item) => item.id === id);
              return <span className="report-loss-person" key={id}>{survivor && <SurvivorPortrait survivor={survivor} detail="thumb" />}<b>{survivor?.name ?? "Unknown survivor"}</b></span>;
            })}
          </div>
        )}
        <button className="primary-button" onClick={onClose}>{report.outcome === "overrun" ? "Choose what survives" : "Return to the bunker"}</button>
      </section>
    </div>
  );
}
